import React, { useState } from 'react';
import { Form, Button, Alert, InputGroup, Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FormContainer from '../../components/form/FormContainer';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import { useForgotPasswordMutation, useResetPasswordMutation } from '../../slices/userApiSlice';
import { FaEye, FaEyeSlash, FaLock, FaEnvelope, FaKey } from 'react-icons/fa';

const ForgotPasswordScreen = () => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();

    const [forgotPassword, { isLoading: loadingForgot }] = useForgotPasswordMutation();
    const [resetPassword, { isLoading: loadingReset }] = useResetPasswordMutation();

    const submitEmailHandler = async (e) => {
        e.preventDefault();
        setMessage(null);
        try {
            await forgotPassword({ email }).unwrap();
            toast.success('Código enviado con éxito. Por favor revisa tu correo.');
            setStep(2);
        } catch (err) {
            toast.error(err?.data?.message || err.error);
            setMessage(err?.data?.message || 'Error al enviar el código');
        }
    };

    const submitResetHandler = async (e) => {
        e.preventDefault();
        setMessage(null);

        if (password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            setMessage('Las contraseñas no coinciden');
            return;
        }

        try {
            await resetPassword({ email, code, newPassword: password }).unwrap();
            toast.success('Contraseña restablecida con éxito');
            navigate('/login');
        } catch (err) {
            toast.error(err?.data?.message || err.error);
            setMessage(err?.data?.message || 'Error al restablecer la contraseña');
        }
    };

    return (
        <FormContainer>
            <Meta title="Recuperar Contraseña" />
            <Card className="shadow-lg border-0 rounded-4 mt-4">
                <Card.Body className="p-4 p-md-5">
                    <div className="text-center mb-4">
                        <div className="mb-3">
                            <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '80px', height: '80px', backgroundColor: '#fff5f2' }}>
                                <FaLock size={35} color="#b5430f" />
                            </div>
                        </div>
                        <h2 className="fw-bold text-dark">
                            {step === 1 ? 'Recuperar Contraseña' : 'Restablecer Contraseña'}
                        </h2>
                        <p className="text-muted">
                            {step === 1 
                                ? 'Ingresa tu correo electrónico y te enviaremos un código de verificación.' 
                                : 'Ingresa el código que recibiste y crea una nueva contraseña.'}
                        </p>
                    </div>

                    {message && <Alert variant="danger" className="rounded-3 border-0 shadow-sm">{message}</Alert>}

                    {step === 1 && (
                        <Form onSubmit={submitEmailHandler}>
                            <Form.Group controlId="email" className="mb-4">
                                <Form.Label column='lg' className="fw-semibold">Correo Electrónico</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text className="bg-white border-end-0 border-secondary-subtle rounded-start-3">
                                        <FaEnvelope className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        placeholder="ejemplo@correo.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="py-2 border-start-0 border-secondary-subtle rounded-end-3"
                                    ></Form.Control>
                                </InputGroup>
                            </Form.Group>

                            <Button 
                                type="submit" 
                                variant="primary" 
                                className="w-100 py-2 rounded-3 fw-bold shadow-sm" 
                                disabled={loadingForgot}
                                style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                            >
                                {loadingForgot ? 'Enviando...' : 'Enviar Código'}
                            </Button>
                            {loadingForgot && <div className="mt-3 text-center"><Loader /></div>}
                        </Form>
                    )}

                    {step === 2 && (
                        <Form onSubmit={submitResetHandler}>
                            <Form.Group controlId="code" className="mb-4">
                                <Form.Label column='lg' className="fw-semibold">Código de Verificación</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text className="bg-white border-end-0 border-secondary-subtle rounded-start-3">
                                        <FaKey className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ingresa el código de 6 dígitos"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        required
                                        className="py-2 border-start-0 border-secondary-subtle rounded-end-3"
                                    ></Form.Control>
                                </InputGroup>
                            </Form.Group>

                            <Form.Group controlId="password" className="mb-4">
                                <Form.Label column='lg' className="fw-semibold">Nueva Contraseña</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Mínimo 6 caracteres"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                    ></Form.Control>
                                    <Button
                                        variant="outline-secondary"
                                        className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <Form.Group controlId="confirmPassword" className="mb-4">
                                <Form.Label column='lg' className="fw-semibold">Confirmar Contraseña</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Repite tu nueva contraseña"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                    ></Form.Control>
                                    <Button
                                        variant="outline-secondary"
                                        className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <Button 
                                type="submit" 
                                variant="primary" 
                                className="w-100 py-2 rounded-3 fw-bold shadow-sm" 
                                disabled={loadingReset}
                                style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                            >
                                {loadingReset ? 'Restableciendo...' : 'Restablecer Contraseña'}
                            </Button>
                            {loadingReset && <div className="mt-3 text-center"><Loader /></div>}
                        </Form>
                    )}

                    <div className="mt-4 text-center">
                        <Link 
                            to="/login" 
                            className="text-decoration-none fw-semibold"
                            style={{ color: '#b5430f' }}
                        >
                            &larr; Volver a Iniciar Sesión
                        </Link>
                    </div>
                </Card.Body>
            </Card>
        </FormContainer>
    );
};

export default ForgotPasswordScreen;
